import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import ProjectCard from "../components/ProjectCard";
import Seo from "../components/Seo";
import { UPCOMING_PROJECTS, PREVIOUS_PROJECTS } from "../data/projects";

function ProjectGroup({ id, label, count, projects, testIdPrefix }) {
  return (
    <section
      id={id}
      data-testid={`${id}-section`}
      className="bg-white border-t border-black/10 py-24 md:py-40"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1 }}
          className="flex items-baseline justify-between mb-16 md:mb-24"
        >
          <span className="label">{label}</span>
          <span className="label hidden sm:inline">{count}</span>
        </motion.div>

        {projects.length === 0 ? (
          <p
            className="font-body font-light italic text-base md:text-lg text-black/50"
            data-testid={`${id}-empty`}
          >
            Nothing to show here yet.
          </p>
        ) : (
          <div className="space-y-24 md:space-y-40">
            {projects.map((p) => (
              <ProjectCard key={p.slug} project={p} testIdPrefix={testIdPrefix} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function Projects() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="projects-page" className="bg-white text-black min-h-screen">
      <Seo
        title="Projects — NYG Projects"
        description="Upcoming and previous NYG Projects exhibitions: Through the Looking Glass by Rebecca Grant de Longueuil at Socialista London, and Shape of Elsewhere at Tramp London."
        image="/share/og-home.jpg"
        imageAlt="NYG Projects — exhibitions programme"
        path="/projects"
      />
      <Nav variant="light" />

      {/* HEADER */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <Link
            to="/"
            data-testid="projects-back-link"
            className="inline-flex items-center gap-2 label hover:opacity-60 transition-opacity"
          >
            <ArrowLeft size={14} strokeWidth={1} /> Back
          </Link>

          <div className="mt-14 md:mt-20">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="label block"
            >
              NYG Projects — Programme
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              data-testid="projects-title"
              className="font-heading text-[52px] sm:text-7xl md:text-8xl lg:text-[140px] leading-[0.9] tracking-[-0.02em] mt-6"
            >
              Our
              <br />
              <span className="italic font-light">Projects</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="font-body font-light italic text-lg md:text-2xl text-black/70 mt-8"
            >
              Intimate pop-up exhibitions shaped by place, connection and experience.
            </motion.p>
          </div>
        </div>
      </section>

      <ProjectGroup
        id="upcoming-projects"
        label="Upcoming Projects"
        count={`${String(UPCOMING_PROJECTS.length).padStart(2, "0")} ${UPCOMING_PROJECTS.length === 1 ? "project" : "projects"}`}
        projects={UPCOMING_PROJECTS}
        testIdPrefix="upcoming"
      />

      <ProjectGroup
        id="previous-projects"
        label="Previous Projects"
        count={`${String(PREVIOUS_PROJECTS.length).padStart(2, "0")} ${PREVIOUS_PROJECTS.length === 1 ? "project" : "projects"}`}
        projects={PREVIOUS_PROJECTS}
        testIdPrefix="previous"
      />

      <Footer />
    </main>
  );
}
